import { useEffect, useState, type ReactNode } from "react";
import { parseMoney, centsToInput, formatMoney } from "../lib/money";

/* ---------- brand ---------- */
/* Two assets: the full lockup where there is width for it, the shield alone
   where there is not. The lockup is unreadable much under 140px wide. */

export function Logo({ width = 202 }: { width?: number }) {
  return (
    <img className="logo" src="/frontlot-logo.png" alt="FrontLot"
         width={width} style={{ width, height: "auto" }} />
  );
}

export function LogoMark({ size = 36 }: { size?: number }) {
  return (
    <img className="logo-mark" src="/frontlot-mark.png" alt="FrontLot"
         width={size} height={size} style={{ width: size, height: "auto" }} />
  );
}

/* ---------- fields ---------- */

type InputRest = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">;

export function Field({
  label, value, onChange, error, hint, mono, ...rest
}: {
  label: string; value: string; onChange: (v: string) => void;
  error?: string | null; hint?: string; mono?: boolean;
} & InputRest) {
  const id = `f-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className={[mono ? "mono" : "", error ? "bad" : value.trim() ? "ok" : ""]
          .filter(Boolean).join(" ")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        {...rest}
      />
      {error ? <p className="msg">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

/** Shows $8,995.00 when you leave it, raw digits while you type. */
export function MoneyField({
  label, value, onChange, error, hint, onBlur,
}: {
  label: string; value: string; onChange: (v: string) => void;
  error?: string | null; hint?: string; onBlur?: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const cents = parseMoney(value);
  // formatMoney divides by 100. Formatting the cents integer directly showed
  // $4,500 as $450,000 — the stored value was right, the display was not.
  const display =
    !focused && cents !== null && value.trim() !== "" ? formatMoney(cents) : value;

  return (
    <div className="field">
      <label htmlFor={`m-${label}`}>{label}</label>
      <div className="moneywrap">
        <span className="dollar">$</span>
        <input
          id={`m-${label}`}
          inputMode="decimal"
          className={error ? "bad" : value.trim() ? "ok" : ""}
          value={focused ? value : display.replace(/^\$/, "")}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            const c = parseMoney(value);
            if (c !== null && value.trim() !== "") onChange(centsToInput(c));
            onBlur?.();
          }}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {error ? <p className="msg">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

/** Formats to (239) 555-1234 as you type; stores digits only. */
export function PhoneField({
  label, value, onChange, error,
}: { label: string; value: string; onChange: (v: string) => void; error?: string | null }) {
  const pretty = (raw: string) => {
    const d = raw.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  return (
    <div className="field">
      <label htmlFor={`p-${label}`}>{label}</label>
      <input
        id={`p-${label}`} type="tel" inputMode="tel" placeholder="(239) 555-1234"
        className={error ? "bad" : value.trim() ? "ok" : ""}
        value={pretty(value)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
      />
      {error && <p className="msg">{error}</p>}
    </div>
  );
}

export function Select({
  label, value, onChange, children, error, hint, placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void; children: ReactNode;
  error?: string | null; hint?: string; placeholder?: string;
}) {
  const id = `s-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
              className={error ? "bad" : value ? "ok" : ""}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {children}
      </select>
      {error ? <p className="msg">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

/** Convenience for the fixed lists in lib/options. */
export function OptionSelect({
  label, value, onChange, options, placeholder = "Choose", error, hint,
}: {
  label: string; value: string; onChange: (v: string) => void;
  options: readonly string[]; placeholder?: string;
  error?: string | null; hint?: string;
}) {
  return (
    <Select label={label} value={value} onChange={onChange}
            placeholder={placeholder} error={error} hint={hint}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </Select>
  );
}

/* ---------- buttons ---------- */

export function Button({
  children, variant = "primary", ...rest
}: { variant?: "primary" | "ghost" | "danger" } &
   React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`btn btn-${variant}`} {...rest}>{children}</button>;
}

/** Says what it makes. A bare + leaves you guessing. */
export function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="addbtn" onClick={onClick}>
      <span aria-hidden="true">+</span>{label}
    </button>
  );
}

/* ---------- bits ---------- */

export type Status = "purchased" | "in_recon" | "available" | "on_hold" | "sold";
const STATUS_LABEL: Record<Status, string> = {
  purchased: "Purchased", in_recon: "In recon", available: "Available",
  on_hold: "On deposit", sold: "Sold",
};
const STATUS_CLASS: Record<Status, string> = {
  purchased: "hold", in_recon: "recon", available: "available",
  on_hold: "hold", sold: "sold",
};
export const Pill = ({ status }: { status: Status }) => (
  <span className={`pill ${STATUS_CLASS[status] ?? "hold"}`}>
    {STATUS_LABEL[status] ?? status}
  </span>
);

export const ErrorNote = ({ children }: { children: ReactNode }) =>
  <p className="errnote">{children}</p>;

export const Empty = ({ children }: { children: ReactNode }) =>
  <div className="empty">{children}</div>;

export function Confirm({
  message, confirmLabel, onConfirm, onCancel, children, danger = true,
  cancelLabel = "Keep it",
}: {
  message?: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Anything the confirmation itself needs answered — a completion date, a
      reason. Without this, "are you sure" and "tell me when it was done"
      become two separate dialogs for one action. */
  children?: ReactNode;
  /** Off for a confirmation that is not destructive, so the red button is
      kept for the ones that actually delete something. */
  danger?: boolean;
  cancelLabel?: string;
}) {
  return (
    <div className="confirm">
      {message && <p>{message}</p>}
      {children}
      <div className="confirm-actions">
        <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
        <Button variant={danger ? "danger" : "primary"} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/* ---------- panel ----------
   A division of a screen. The header carries the section's own headline
   figure, so the page can be read by scanning the right-hand column rather
   than every row. `rail` says what kind of section it is; see app.css. */

export function Panel({
  title, figure, note, rail = "record", children, foot,
}: {
  title: string;
  figure?: React.ReactNode;
  note?: string;
  rail?: "money" | "work" | "keep" | "danger" | "record";
  children: React.ReactNode;
  foot?: React.ReactNode;
}) {
  return (
    <section className={`panel ${rail}`}>
      <div className="panel-head">
        <h3>{title}</h3>
        {figure !== undefined && <span className="panel-figure">{figure}</span>}
        {figure === undefined && note && <span className="panel-note">{note}</span>}
      </div>
      <div className="panel-body">{children}</div>
      {foot && <div className="panel-foot">{foot}</div>}
    </section>
  );
}

/* ---------- pager ----------
   Lists page at a fixed size rather than running on forever. The count is of
   the filtered set, not the table, so "31 to 60 of 214" answers the question
   a person actually has: how much more is there of what I asked for. */

export const PAGE_SIZE = 30;

export function Pager({ page, setPage, total }: {
  page: number; setPage: (n: number) => void; total: number;
}) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  const first = page * PAGE_SIZE + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);
  return (
    <div className="pager">
      <button className="btn btn-ghost" disabled={page === 0}
              onClick={() => setPage(page - 1)}>Previous</button>
      <span className="pager-count">
        {first}–{last} of {total}
      </span>
      <button className="btn btn-ghost" disabled={page >= pages - 1}
              onClick={() => setPage(page + 1)}>Next</button>
    </div>
  );
}

/** The slice to render, clamped so a page that no longer exists — after a
    filter narrows the list — shows the last one instead of nothing. */
export function pageSlice<T>(rows: T[], page: number): T[] {
  const start = Math.min(page, Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1)) * PAGE_SIZE;
  return rows.slice(start, start + PAGE_SIZE);
}

/* ---------- lightbox ----------
   A photo at full size, with the keyboard working: arrows to move, escape to
   close. Focus is trapped on the dialog and returned when it closes. */

export function Lightbox({
  src, caption, index, count, onPrev, onNext, onClose,
}: {
  src: string; caption?: string;
  index: number; count: number;
  onPrev: () => void; onNext: () => void; onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    // Stop the page behind from scrolling under the photo.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="lightbox" role="dialog" aria-modal="true"
         aria-label={caption || `Photo ${index + 1} of ${count}`}
         onClick={onClose}>
      <img src={src} alt={caption ?? ""} onClick={(e) => e.stopPropagation()} />
      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-btn" onClick={onPrev}
                disabled={count < 2} aria-label="Previous photo">‹</button>
        <span className="lightbox-count">{index + 1} of {count}</span>
        <button className="lightbox-btn" onClick={onNext}
                disabled={count < 2} aria-label="Next photo">›</button>
      </div>
      <button className="lightbox-close" onClick={onClose} aria-label="Close">✕</button>
    </div>
  );
}
